import { useLocation, useNavigate } from 'react-router-dom'

import { Moment } from 'moment'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import { useAttendanceDateNavigator } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { SidePanelTab } from 'modules/attendance/lib/sidepanelTabTypes'

export type TabType = {
  id: SidePanelTab
  label: string
  url: string
}

type Props = {
  tabFromNavigation: SidePanelTab
  compensationEnabled: boolean
  timeSheetDate: Moment
  employeeId: number
}

export const useEmployeeSidepanelNavigation = ({
  tabFromNavigation,
  compensationEnabled,
  timeSheetDate: _timeSheetDate,
  employeeId,
}: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const location = useLocation()
  const { pathParams } = useAttendanceDateNavigator()

  const params = {
    ...pathParams,
    employeeId: employeeId,
  }

  const clockInPage = location.pathname.includes(navigation.clockIn.path)

  let attendanceTimesheetUrl =
    navigation.timeTracking.employee.periodTypeYearMonthDayEmployeeId.fullPath(params)
  let workedHoursUrl =
    navigation.timeTracking.employee.periodTypeYearMonthDayEmployeeId.workedHoursBreakdownEmployeeId.fullPath(
      params
    )
  let balanceUrl =
    navigation.timeTracking.employee.periodTypeYearMonthDayEmployeeId.balanceBreakdownEmployeeId.fullPath(
      params
    )
  let compensationUrl =
    navigation.timeTracking.employee.periodTypeYearMonthDayEmployeeId.compensationBreakdownEmployeeId.fullPath(
      params
    )

  let bankOfHoursUrl =
    navigation.timeTracking.employee.periodTypeYearMonthDayEmployeeId.bankOfHoursEmployeeId.fullPath(
      params
    )

  if (clockInPage) {
    attendanceTimesheetUrl = navigation.clockIn.periodTypeYearMonthDay.fullPath(params)
    workedHoursUrl =
      navigation.clockIn.periodTypeYearMonthDay.workedHoursBreakdownEmployeeId.fullPath(params)
    balanceUrl =
      navigation.clockIn.periodTypeYearMonthDay.balanceBreakdownEmployeeId.fullPath(params)
    compensationUrl =
      navigation.clockIn.periodTypeYearMonthDay.compensationBreakdownEmployeeId.fullPath(params)
    bankOfHoursUrl =
      navigation.clockIn.periodTypeYearMonthDay.bankOfHoursEmployeeId.fullPath(params)
  }

  const tabs: TabType[] = [
    {
      id: 'worked_hours',
      label: i18n.t('clock_in.sidepanel.tabs.worked_hours'),
      url: workedHoursUrl,
    },
  ]

  tabs.push({
    id: 'balance',
    label: i18n.t('clock_in.sidepanel.tabs.balance'),
    url: balanceUrl,
  })

  if (compensationEnabled) {
    tabs.push({
      id: 'compensation',
      label: i18n.t('clock_in.sidepanel.tabs.compensation'),
      url: compensationUrl,
    })
  }

  if (hasFeature('dev_time_tracking_bank_of_hours')) {
    tabs.push({
      id: 'bank_of_hours',
      label: i18n.t('clock_in.bank_of_hours.button.label'),
      url: bankOfHoursUrl,
    })
  }

  const handleTabClick = (tabId: SidePanelTab) => {
    const tab = tabs.find((t) => t.id === tabId)
    if (tab) {
      navigate(tab.url)
    }
  }

  const closePanel = () => {
    navigate(attendanceTimesheetUrl)
  }

  return {
    handleTabClick,
    closePanel,
    selectedTab: tabFromNavigation,
    tabs,
  }
}
